import type {NotificationMenuDataType} from '@sdb/ui/types/components/molecules/NotificationMenu.types';
import type {MinervaSettings} from '@/shared/types/MinervaSettings';

type TaskItem = NonNullable<NotificationMenuDataType['items']>[number];

export interface TaskItemCounts {
    authorizationRequests?: number;
    assessmentRequests?: number;
    portfolioTasks?: number;
    upcomingTrainingsTeacher?: number;
    upcomingTrainingsParticipant?: number;
    upcomingPresence?: number;
    evaluations?: number;
}

export interface TaskItemsResult {
    items: TaskItem[];
    totalCount: number;
}

export default function getTaskItems(
    t: (key: string) => string,
    navigate: (url: string) => void,
    settings: MinervaSettings | null = {},
    counts: TaskItemCounts = {},
): TaskItemsResult {
    const taskItems: TaskItem[] = [];
    settings ??= {};

    if (settings.classroomEnabled) {
        if (counts.authorizationRequests) {
            taskItems.push({
                label: t('authorizationRequests'),
                payload: () => navigate('/catalogAuthorizations'),
                badge: {
                    color: 'warning' as const,
                    // CardMenuItem draws a dot unless the variant says otherwise; standard shows the count.
                    variant: 'standard' as const,
                    badgeContent: counts.authorizationRequests,
                },
            });
        }
    }

    if (settings.skillsEnabled) {
        if (counts.assessmentRequests) {
            taskItems.push({
                label: t('assessmentRequests'),
                payload: () => navigate('/examinerreview/request'),
                badge: {
                    color: 'warning' as const,
                    variant: 'standard' as const,
                    badgeContent: counts.assessmentRequests,
                },
            });
        }
    }

    if (settings.expertiseEnabled) {
        if (counts.portfolioTasks) {
            taskItems.push({
                label: t('portfolioTasks'),
                payload: () => navigate('/expertisetasks/reviewer'),
                badge: {
                    color: 'warning' as const,
                    variant: 'standard' as const,
                    badgeContent: counts.portfolioTasks,
                },
            });
        }
    }

    if (settings.classroomEnabled) {
        if (counts.upcomingTrainingsTeacher) {
            taskItems.push({
                label: t('upcomingTrainingsTeacher'),
                payload: () => navigate('/upcomingteacher'),
                badge: {
                    color: 'warning' as const,
                    variant: 'standard' as const,
                    badgeContent: counts.upcomingTrainingsTeacher,
                },
            });
        }

        if (counts.upcomingTrainingsParticipant) {
            taskItems.push({
                label: t('upcomingTrainingsParticipant'),
                payload: () => navigate('/user/my/enrollments'),
                badge: {
                    color: 'warning' as const,
                    variant: 'standard' as const,
                    badgeContent: counts.upcomingTrainingsParticipant,
                },
            });
        }

        if (counts.upcomingPresence) {
            taskItems.push({
                label: t('unprocessedPresence'),
                payload: () => navigate('/upcomingpresence'),
                badge: {
                    color: 'warning' as const,
                    variant: 'standard' as const,
                    badgeContent: counts.upcomingPresence,
                },
            });
        }

        if (settings.evaluationEnabled && counts.evaluations) {
            taskItems.push({
                label: t('evaluations'),
                payload: () => navigate('/participantEvaluations'),
                badge: {
                    color: 'warning' as const,
                    variant: 'standard' as const,
                    badgeContent: counts.evaluations,
                },
            });
        }
    }

    const totalCount = Object.values(counts).reduce((sum, count) => sum + (count || 0), 0);

    return {
        items: taskItems,
        totalCount,
    };
}
